import React, { useEffect, useState } from "react";
import { api, auth } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Textarea, Pill } from "../components/UI";
import { toast } from "sonner";
import {
  ScrollText, FileCheck2, ShieldCheck, Download, Loader2, Copy,
  BadgeCheck, XCircle, Plus, FileUp, Clapperboard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const TABS = [
  { key: "rules", label: "Rules", icon: ScrollText },
  { key: "disclaimer", label: "Disclaimer Builder", icon: FileCheck2 },
  { key: "verification", label: "Entity Verification", icon: ShieldCheck },
  { key: "adcreatives", label: "Ad Creatives", icon: Clapperboard },
  { key: "export", label: "Audit Vault Export", icon: Download },
];

export default function ComplianceCenter() {
  const user = auth.getUser();
  const navigate = useNavigate();
  const canReview = ["super_admin", "admin", "compliance_reviewer"].includes(user?.role);
  const [tab, setTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [ruleForm, setRuleForm] = useState(null);
  const [entities, setEntities] = useState([]);
  const [disclaimerForm, setDisclaimerForm] = useState({ political_entity_id: "", committee_name: "", jurisdiction: "Federal", medium: "ctv", authorized_by_candidate: true, candidate_name: "", state_registration_number: "" });
  const [disclaimer, setDisclaimer] = useState(null);
  const [building, setBuilding] = useState(false);
  const [verForm, setVerForm] = useState({ political_entity_id: "", doc_type: "FEC Statement of Organization", filename: "", file_base64: "", note: "" });
  const [verDocs, setVerDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [adReviews, setAdReviews] = useState([]);

  const loadAdReviews = () => {
    if (canReview) api.get("/ad-studio/reviews").then(r => setAdReviews(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get(`/compliance/rules?include_history=${showHistory}`).then(r => setRules(r.data));
    api.get("/political/entities").then(r => setEntities(r.data)).catch(() => {});
    if (canReview) api.get("/compliance/verification").then(r => setVerDocs(r.data)).catch(() => {});
    loadAdReviews();
  }, [showHistory]); // eslint-disable-line

  const reviewAd = async (id, decision) => {
    try {
      await api.post(`/ad-studio/reviews/${id}/decision`, { decision });
      toast.success(`Creative package ${decision}`);
      loadAdReviews();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveRule = async () => {
    try {
      await api.post("/compliance/rules", ruleForm);
      toast.success("New rule version published");
      setRuleForm(null);
      const r = await api.get(`/compliance/rules?include_history=${showHistory}`); setRules(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const buildDisclaimer = async () => {
    setBuilding(true);
    try {
      const r = await api.post("/compliance/disclaimer", disclaimerForm);
      setDisclaimer(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBuilding(false); }
  };

  const onVerFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setVerForm(v => ({ ...v, filename: f.name, file_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const uploadVerification = async () => {
    if (!verForm.political_entity_id || !verForm.file_base64) { toast.error("Select an entity and a document"); return; }
    setUploading(true);
    try {
      await api.post("/compliance/verification", verForm);
      toast.success("Verification document submitted for review");
      setVerForm({ political_entity_id: "", doc_type: "FEC Statement of Organization", filename: "", file_base64: "", note: "" });
      if (canReview) { const r = await api.get("/compliance/verification"); setVerDocs(r.data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const reviewDoc = async (id, action) => {
    try {
      await api.post(`/compliance/verification/${id}/${action}`);
      toast.success(`Document ${action}d`);
      const r = await api.get("/compliance/verification"); setVerDocs(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const download = async (format) => {
    try {
      const r = await api.get(`/compliance/audit-export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `audit_vault.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Political Compliance Engine"
        title="Compliance Center"
        subtitle="Versioned rules, jurisdictional disclaimer builder, entity verification, and exportable audit vault."
      />

      <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`compliance-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.key ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "rules" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-xs text-slate-400 flex items-center gap-2">
              <input type="checkbox" checked={showHistory} onChange={e => setShowHistory(e.target.checked)} data-testid="rules-history-toggle" />
              Show version history
            </label>
            {canReview && (
              <Btn variant="secondary" onClick={() => setRuleForm({ key: "", title: "", jurisdiction: "Platform", body: "", applies_to: ["ctv"] })} data-testid="new-rule-btn">
                <Plus className="w-4 h-4" /> New rule / version
              </Btn>
            )}
          </div>
          {ruleForm && (
            <Card className="border-cyan-400/30">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Rule key (versioned by key)" value={ruleForm.key} onChange={e => setRuleForm({ ...ruleForm, key: e.target.value })} placeholder="federal_disclaimer" data-testid="rule-key-input" />
                <Input label="Title" value={ruleForm.title} onChange={e => setRuleForm({ ...ruleForm, title: e.target.value })} className="md:col-span-2" data-testid="rule-title-input" />
                <Select label="Jurisdiction" value={ruleForm.jurisdiction} onChange={e => setRuleForm({ ...ruleForm, jurisdiction: e.target.value })}>
                  {["Federal", "State", "Platform"].map(j => <option key={j}>{j}</option>)}
                </Select>
                <Textarea label="Rule body" value={ruleForm.body} onChange={e => setRuleForm({ ...ruleForm, body: e.target.value })} className="md:col-span-2" data-testid="rule-body-input" />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Btn variant="secondary" onClick={() => setRuleForm(null)}>Cancel</Btn>
                <Btn onClick={saveRule} data-testid="save-rule-btn">Publish version</Btn>
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rules-list">
            {rules.map(r => (
              <Card key={r.id} className={r.status === "superseded" ? "opacity-50" : ""}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold">{r.jurisdiction} · v{r.version}</div>
                    <div className="font-display text-base font-bold mt-0.5">{r.title}</div>
                  </div>
                  <Pill status={r.status === "active" ? "Active" : "Completed"}>{r.status}</Pill>
                </div>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{r.body}</p>
                <div className="mt-2 flex gap-1">
                  {(r.applies_to || []).map(a => <span key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400 uppercase">{a}</span>)}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "disclaimer" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Build a compliant disclaimer</div>
            <div className="space-y-3">
              <Select label="Political entity" value={disclaimerForm.political_entity_id} onChange={e => setDisclaimerForm({ ...disclaimerForm, political_entity_id: e.target.value })} data-testid="disclaimer-entity-select">
                <option value="">Custom committee name…</option>
                {entities.map(en => <option key={en.id} value={en.id}>{en.public_campaign_name || en.legal_org_name}</option>)}
              </Select>
              {!disclaimerForm.political_entity_id && (
                <Input label="Committee name" value={disclaimerForm.committee_name} onChange={e => setDisclaimerForm({ ...disclaimerForm, committee_name: e.target.value })} placeholder="Friends of Jane Voss" data-testid="disclaimer-committee-input" />
              )}
              <div className="grid grid-cols-2 gap-3">
                <Select label="Jurisdiction" value={disclaimerForm.jurisdiction} onChange={e => setDisclaimerForm({ ...disclaimerForm, jurisdiction: e.target.value })} data-testid="disclaimer-jurisdiction-select">
                  {["Federal", "State"].map(j => <option key={j}>{j}</option>)}
                </Select>
                <Select label="Medium" value={disclaimerForm.medium} onChange={e => setDisclaimerForm({ ...disclaimerForm, medium: e.target.value })} data-testid="disclaimer-medium-select">
                  {["ctv", "video", "display", "audio"].map(m => <option key={m}>{m}</option>)}
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={disclaimerForm.authorized_by_candidate} onChange={e => setDisclaimerForm({ ...disclaimerForm, authorized_by_candidate: e.target.checked })} data-testid="disclaimer-authorized-toggle" />
                Authorized by the candidate / candidate's committee
              </label>
              {disclaimerForm.authorized_by_candidate && (
                <Input label="Candidate name (optional 'Approved by')" value={disclaimerForm.candidate_name} onChange={e => setDisclaimerForm({ ...disclaimerForm, candidate_name: e.target.value })} placeholder="Jane Voss" />
              )}
              {disclaimerForm.jurisdiction === "State" && (
                <Input label="State registration #" value={disclaimerForm.state_registration_number} onChange={e => setDisclaimerForm({ ...disclaimerForm, state_registration_number: e.target.value })} placeholder="C-12345" />
              )}
              <Btn onClick={buildDisclaimer} disabled={building} className="w-full justify-center" data-testid="build-disclaimer-btn">
                {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />} Generate disclaimer
              </Btn>
            </div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Result</div>
            {disclaimer ? (
              <div data-testid="disclaimer-result">
                <div className="rounded-md bg-slate-900/80 border border-cyan-400/30 p-4 font-mono text-sm text-white" data-testid="disclaimer-text">
                  {disclaimer.disclaimer_text}
                </div>
                <button onClick={() => { navigator.clipboard.writeText(disclaimer.disclaimer_text); toast.success("Copied"); }}
                  className="mt-2 text-xs text-cyan-300 flex items-center gap-1 hover:underline" data-testid="copy-disclaimer-btn">
                  <Copy className="w-3 h-3" /> Copy text
                </button>
                <div className="mt-4 text-xs uppercase tracking-wider text-slate-400 font-medium">Placement requirements</div>
                <ul className="mt-2 space-y-1.5">
                  {disclaimer.requirements.map((r, i) => (
                    <li key={i} className="text-xs text-slate-300 flex gap-2"><BadgeCheck className="w-3.5 h-3.5 text-cyan-300 shrink-0 mt-0.5" />{r}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-slate-500">Generated disclaimer text + placement requirements will appear here.</div>
            )}
          </Card>
        </div>
      )}

      {tab === "verification" && (
        <div className="space-y-4">
          <Card>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Submit verification documents</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select label="Political entity" value={verForm.political_entity_id} onChange={e => setVerForm({ ...verForm, political_entity_id: e.target.value })} data-testid="ver-entity-select">
                <option value="">Select entity…</option>
                {entities.map(en => <option key={en.id} value={en.id}>{en.public_campaign_name || en.legal_org_name}</option>)}
              </Select>
              <Select label="Document type" value={verForm.doc_type} onChange={e => setVerForm({ ...verForm, doc_type: e.target.value })} data-testid="ver-doctype-select">
                {["FEC Statement of Organization", "State committee registration", "EIN letter", "Authorization letter", "Other"].map(d => <option key={d}>{d}</option>)}
              </Select>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Document (PDF/image)</label>
                <label className="flex items-center gap-2 border border-dashed border-white/15 rounded-md px-3 py-2 text-sm text-slate-400 cursor-pointer hover:border-cyan-400/40" data-testid="ver-file-input">
                  <FileUp className="w-4 h-4" /> {verForm.filename || "Choose file…"}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onVerFile} />
                </label>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Btn onClick={uploadVerification} disabled={uploading} data-testid="ver-submit-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Submit for review
              </Btn>
            </div>
          </Card>

          {canReview && (
            <Card padded={false}>
              <div className="px-5 py-3 border-b border-white/10">
                <h3 className="font-display text-lg font-bold">Review queue</h3>
              </div>
              <table className="w-full text-sm" data-testid="verification-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="px-5 py-3">Entity</th>
                    <th className="px-5 py-3">Document</th>
                    <th className="px-5 py-3">Uploaded by</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 text-right">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {verDocs.map(d => (
                    <tr key={d.id} className="border-b border-white/5">
                      <td className="px-5 py-2.5 text-white">{d.entity_name}</td>
                      <td className="px-5 py-2.5 text-xs text-slate-300">{d.doc_type}<div className="text-slate-600">{d.filename}</div></td>
                      <td className="px-5 py-2.5 text-xs text-slate-400">{d.uploaded_by}</td>
                      <td className="px-5 py-2.5"><Pill status={d.status === "approved" ? "Active" : d.status === "rejected" ? "Rejected" : "Pending approval"}>{d.status}</Pill></td>
                      <td className="px-5 py-2.5 text-right">
                        {d.status === "pending" && (
                          <div className="flex justify-end gap-2">
                            <Btn variant="secondary" className="!px-2.5 !py-1 text-xs" onClick={() => reviewDoc(d.id, "approve")} data-testid={`ver-approve-${d.id}`}><BadgeCheck className="w-3.5 h-3.5 text-emerald-300" /> Approve</Btn>
                            <Btn variant="danger" className="!px-2.5 !py-1 text-xs" onClick={() => reviewDoc(d.id, "reject")} data-testid={`ver-reject-${d.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</Btn>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {verDocs.length === 0 && <tr><td colSpan="5" className="px-5 py-6 text-center text-sm text-slate-500">No verification documents yet.</td></tr>}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {tab === "adcreatives" && (
        <Card padded={false}>
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-bold">Ad Studio creative reviews</h3>
              <p className="text-xs text-slate-500 mt-0.5">AI-generated ad packages must be approved here before they can be pushed live to a campaign.</p>
            </div>
          </div>
          {canReview ? (
            <table className="w-full text-sm" data-testid="ad-reviews-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3">Project</th>
                  <th className="px-5 py-3">Candidate / Committee</th>
                  <th className="px-5 py-3">Assets</th>
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Review</th>
                </tr>
              </thead>
              <tbody>
                {adReviews.map(r => (
                  <tr key={r.id} className="border-b border-white/5">
                    <td className="px-5 py-2.5">
                      <button onClick={() => navigate(`/ad-studio/${r.project_id}`)} className="text-white hover:text-cyan-300 text-left" data-testid={`ad-review-open-${r.id}`}>
                        {r.project_name}
                      </button>
                      <div className="text-[10px] text-slate-600">{r.disclaimer}</div>
                    </td>
                    <td className="px-5 py-2.5 text-xs text-slate-300">{r.candidate_name}<div className="text-slate-600">{r.committee_name}</div></td>
                    <td className="px-5 py-2.5 text-xs text-slate-400">
                      {r.summary?.videos || 0} video · {r.summary?.voiceovers || 0} audio · {r.summary?.banners || 0} banners{r.summary?.spanish ? " · ES" : ""}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-slate-400">{r.submitted_by}<div className="text-slate-600">{new Date(r.submitted_at).toLocaleString()}</div></td>
                    <td className="px-5 py-2.5"><Pill status={r.status === "approved" ? "Active" : r.status === "rejected" ? "Rejected" : "Pending approval"}>{r.status}</Pill></td>
                    <td className="px-5 py-2.5 text-right">
                      {r.status === "pending" && (
                        <div className="flex justify-end gap-2">
                          <Btn variant="secondary" className="!px-2.5 !py-1 text-xs" onClick={() => reviewAd(r.id, "approved")} data-testid={`ad-review-approve-${r.id}`}><BadgeCheck className="w-3.5 h-3.5 text-emerald-300" /> Approve</Btn>
                          <Btn variant="danger" className="!px-2.5 !py-1 text-xs" onClick={() => reviewAd(r.id, "rejected")} data-testid={`ad-review-reject-${r.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</Btn>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {adReviews.length === 0 && <tr><td colSpan="6" className="px-5 py-6 text-center text-sm text-slate-500">No creative packages submitted for review yet.</td></tr>}
              </tbody>
            </table>
          ) : (
            <div className="px-5 py-8 text-center text-sm text-slate-500">Compliance reviewer or admin role required to review creative packages.</div>
          )}
        </Card>
      )}

      {tab === "export" && (
        <Card>
          <h3 className="font-display text-lg font-bold">Political Audit Vault</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">Every campaign action, approval, sync, disclaimer, and verification event is logged immutably. Export the full vault for counsel, audits, or FEC inquiries.</p>
          <div className="mt-4 flex gap-2">
            <Btn onClick={() => download("csv")} data-testid="export-csv-btn"><Download className="w-4 h-4" /> Export CSV</Btn>
            <Btn variant="secondary" onClick={() => download("json")} data-testid="export-json-btn"><Download className="w-4 h-4" /> Export JSON</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
